import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CustomTabs } from '@/components/ui/custom-tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { 
  ArrowLeft, 
  Users, 
  Calendar, 
  DollarSign, 
  Phone, 
  MapPin, 
  Clock,
  CheckCircle2,
  AlertTriangle,
  Plus,
  UserPlus,
  X
} from 'lucide-react'
import { mockMinisterios, mockMembros, mockEventos, getEscalasByMinisterio } from '@/mocks'
import { slugify } from '@/lib/utils'
import { FinancasMinisterio } from '@/pages/financas/ministerio-detalhe'
import { EscalaMinisterio } from '@/components/escala/escala-ministerio'

export function MinisterioDetalhePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('membros')
  
  // Estados para controlar os cards
  const [showCriarFuncoesCard, setShowCriarFuncoesCard] = useState(false)
  const [showAdicionarMembrosCard, setShowAdicionarMembrosCard] = useState(false)
  const [showNovaEscalacaoForm, setShowNovaEscalacaoForm] = useState(false)
  const [funcoes, setFuncoes] = useState<string[]>([])
  const [novaFuncao, setNovaFuncao] = useState('')
  const [membroSelecionado, setMembroSelecionado] = useState('')
  const [searchMembro, setSearchMembro] = useState('')

  // Verificar se há um parâmetro tab na URL
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['membros', 'eventos', 'escala', 'financas', 'funcoes'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  const ministerio = mockMinisterios.find(m => m.id === id)
  
  if (!ministerio) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <h2 className="text-2xl font-bold">Ministério não encontrado</h2>
        <Button onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Ministérios
        </Button>
      </div>
    )
  }

  // Filtrar membros e eventos do ministério (simulação)
  const membrosMinisterioOriginal = mockMembros.filter(() => Math.random() > 0.7).slice(0, 6)
  const membrosMinisterio = membrosMinisterioOriginal.map(perfil => ({
    id: perfil.id,
    nome: perfil.nome_exibicao,
    email: '', // Não disponível no Perfil
    telefone: perfil.telefone || undefined,
    cargo: perfil.papel,
    foto: perfil.avatar_url || undefined
  }))
  const eventosMinisterio = mockEventos.filter(evento => 
    evento.ministerio_id === ministerio.id
  )
  const escalasMinisterio = getEscalasByMinisterio(ministerio.id)

  // Funções para gerenciar os cards
  const handleCriarFuncoes = () => {
    setShowCriarFuncoesCard(true)
    setShowAdicionarMembrosCard(false)
    setShowNovaEscalacaoForm(false)
  }

  const handleAdicionarMembros = () => {
    setShowAdicionarMembrosCard(true)
    setShowCriarFuncoesCard(false)
    setShowNovaEscalacaoForm(false)
  }

  const handleNovaEscalacao = () => {
    setShowNovaEscalacaoForm(true)
    setShowCriarFuncoesCard(false)
    setShowAdicionarMembrosCard(false)
  }

  const handleAdicionarFuncao = () => {
    if (novaFuncao.trim()) {
      setFuncoes(prev => [...prev, novaFuncao.trim()])
      setNovaFuncao('')
    }
  }

  const handleCancelarCard = () => {
    setShowCriarFuncoesCard(false)
    setShowAdicionarMembrosCard(false)
    setShowNovaEscalacaoForm(false)
    setNovaFuncao('')
    setMembroSelecionado('')
    setSearchMembro('')
  }

  const handleAdicionarMembroAoMinisterio = () => {
    if (membroSelecionado) {
      // Aqui você implementaria a lógica para adicionar o membro ao ministério
      console.log('Adicionando membro:', membroSelecionado)
      handleCancelarCard()
    }
  }

  // Filtrar membros baseado na pesquisa
  const membrosDisponiveis = mockMembros.filter(membro =>
    membro.nome_exibicao.toLowerCase().includes(searchMembro.toLowerCase()) ||
    membro.papel.toLowerCase().includes(searchMembro.toLowerCase())
  )

  const tabs = [
    { id: 'membros', label: 'Membros', count: membrosMinisterioOriginal.length },
    { id: 'funcoes', label: 'Funções', count: funcoes.length },
    { id: 'eventos', label: 'Eventos', count: eventosMinisterio.length },
    { id: 'escala', label: 'Escala', count: escalasMinisterio.length },
    { id: 'financas', label: 'Finanças' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Ministérios
        </Button>
      </div>

      {/* Informações do Ministério */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl">{ministerio.nome}</CardTitle>
              <CardDescription className="mt-2">{ministerio.descricao}</CardDescription>
            </div>
            <Badge variant={ministerio.ativo ? 'default' : 'secondary'}>
              {ministerio.ativo ? (
                <>
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Ativo
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Inativo
                </>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{membrosMinisterio.length} membros</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{escalasMinisterio.length} escalas</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Orçamento disponível</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <CustomTabs tabs={tabs} activeTab={activeTab} onTabClick={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'membros' && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Botões de Ação */}
          <div className="flex gap-3">
            <Button onClick={handleAdicionarMembros} className="shadow-md shadow-primary/20">
              <UserPlus className="h-4 w-4 mr-2" />
              Adicionar Membros
            </Button>
          </div>

          {/* Card Adicionar Membros */}
          {showAdicionarMembrosCard && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Adicionar Membro ao Ministério</CardTitle>
                    <Button variant="ghost" size="sm" onClick={handleCancelarCard}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="membro-select">Selecionar Membro</Label>
                    <Select value={membroSelecionado} onValueChange={setMembroSelecionado}>
                      <SelectTrigger>
                        <SelectValue placeholder="Pesquisar e selecionar membro..." />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="p-2">
                          <Input
                            placeholder="Digite para pesquisar..."
                            value={searchMembro}
                            onChange={(e) => setSearchMembro(e.target.value)}
                            className="mb-2"
                          />
                        </div>
                        {membrosDisponiveis.length > 0 ? (
                          membrosDisponiveis.map((membro) => (
                            <SelectItem key={membro.id} value={membro.id}>
                              <div className="flex items-center gap-2">
                                <span>{membro.nome_exibicao}</span>
                                <span className="text-xs text-muted-foreground">({membro.papel})</span>
                              </div>
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="sem-membros" disabled>
                            {searchMembro ? 'Nenhum membro encontrado' : 'Digite para pesquisar membros'}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleAdicionarMembroAoMinisterio}
                      disabled={!membroSelecionado}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Adicionar Membro
                    </Button>
                    <Button variant="outline" onClick={handleCancelarCard}>
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {membrosMinisterioOriginal.map((membro) => (
              <Card key={membro.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-3">
                    <Avatar>
                      <AvatarImage src={membro.avatar_url || undefined} />
                      <AvatarFallback>
                        {membro.nome_exibicao.split(' ').map((n: string) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-base">{membro.nome_exibicao}</CardTitle>
                      <CardDescription>{membro.papel}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {membro.telefone && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      <span>{membro.telefone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>Desde {membro.created_at ? new Date(membro.created_at).toLocaleDateString() : 'Data não disponível'}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
            {membrosMinisterioOriginal.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum membro encontrado neste ministério.
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'eventos' && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {eventosMinisterio.map((evento) => (
              <Card key={evento.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{evento.titulo}</CardTitle>
                      <CardDescription>{evento.descricao}</CardDescription>
                    </div>
                    <Badge variant={evento.status === 'publicado' ? 'default' : 'secondary'}>
                      {evento.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{new Date(evento.inicio_em).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>{new Date(evento.inicio_em).toLocaleTimeString()}</span>
                  </div>
                  {evento.local && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span>{evento.local}</span>
                    </div>
                  )}
                  {evento.capacidade && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>Até {evento.capacidade} participantes</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {eventosMinisterio.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum evento encontrado para este ministério.
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'escala' && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Botões de Ação */}
          <div className="flex gap-3">
            <Button onClick={handleNovaEscalacao} className="shadow-md shadow-primary/20">
              <Plus className="h-4 w-4 mr-2" />
              Nova Escalação
            </Button>
          </div>

          <EscalaMinisterio 
            ministerio={ministerio} 
            membros={membrosMinisterio} 
            escalasExistentes={escalasMinisterio}
            funcoesCadastradas={funcoes}
            showForm={showNovaEscalacaoForm}
            onToggleForm={setShowNovaEscalacaoForm}
          />
        </motion.div>
      )}

      {activeTab === 'funcoes' && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Botão de Ação */}
          <div className="flex gap-3">
            <Button onClick={handleCriarFuncoes} className="shadow-md shadow-primary/20">
              <Plus className="h-4 w-4 mr-2" />
              Criar Funções
            </Button>
          </div>

          {/* Card Criar Funções */}
          {showCriarFuncoesCard && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Criar Nova Função</CardTitle>
                    <Button variant="ghost" size="sm" onClick={handleCancelarCard}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome-funcao">Nome da Função</Label>
                    <Input
                      id="nome-funcao"
                      placeholder="Ex: Vocalista, Guitarrista, Tecladista..."
                      value={novaFuncao}
                      onChange={(e) => setNovaFuncao(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAdicionarFuncao()}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAdicionarFuncao} disabled={!novaFuncao.trim()}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar
                    </Button>
                    <Button variant="outline" onClick={handleCancelarCard}>
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Cards das Funções Criadas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {funcoes.map((funcao, index) => (
              <Card key={index}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{funcao}</CardTitle>
                    <Badge variant="secondary">Função</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-3 w-3" />
                    <span>Disponível para escala</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-3 w-3" />
                    <span>Ativa</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {funcoes.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma função criada ainda. Clique em "Criar Funções" para começar.
            </div>
          )}
        </motion.div>
      )}

      {activeTab === 'financas' && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <FinancasMinisterio ministerioNome={slugify(ministerio.nome)} />
        </motion.div>
      )}
    </div>
  )
}